import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DollarSign, Coins, Save, Info, Truck, Building2, X, Plus } from "lucide-react";

export default function Settings() {
  const [insp, setInsp] = useState(null);
  const [inspForm, setInspForm] = useState({ inspection_charge_usd: "", toll_vann_ghc: "" });
  const [truck, setTruck] = useState(null);
  const [truckForm, setTruckForm] = useState({ rate_20ft_usd: "", rate_40ft_usd: "" });
  const [lines, setLines] = useState([]);
  const [newLine, setNewLine] = useState("");
  const [savingInsp, setSavingInsp] = useState(false);
  const [savingTruck, setSavingTruck] = useState(false);
  const [savingLines, setSavingLines] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2, r3] = await Promise.all([
          api.get("/settings/rates"),
          api.get("/settings/trucking-rates"),
          api.get("/settings/shipping-lines"),
        ]);
        setInsp(r1.data);
        setInspForm({
          inspection_charge_usd: String(r1.data.inspection_charge_usd ?? 4),
          toll_vann_ghc: String(r1.data.toll_vann_ghc ?? 35),
        });
        setTruck(r2.data);
        setTruckForm({
          rate_20ft_usd: String(r2.data.rate_20ft_usd ?? 15),
          rate_40ft_usd: String(r2.data.rate_40ft_usd ?? 30),
        });
        setLines(r3.data.values || []);
      } catch {
        showToast("Failed to load settings", "err");
      }
    })();
  }, []);

  const submitInsp = async (e) => {
    e.preventDefault();
    const usd = parseFloat(inspForm.inspection_charge_usd);
    const ghc = parseFloat(inspForm.toll_vann_ghc);
    if (isNaN(usd) || isNaN(ghc) || usd < 0 || ghc < 0) {
      showToast("Inspection rates must be valid non-negative numbers", "err"); return;
    }
    setSavingInsp(true);
    try {
      const r = await api.put("/settings/rates", { inspection_charge_usd: usd, toll_vann_ghc: ghc });
      setInsp(r.data);
      showToast("Inspection rates updated");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Save failed", "err");
    } finally {
      setSavingInsp(false);
    }
  };

  const submitTruck = async (e) => {
    e.preventDefault();
    const r20 = parseFloat(truckForm.rate_20ft_usd);
    const r40 = parseFloat(truckForm.rate_40ft_usd);
    if (isNaN(r20) || isNaN(r40) || r20 < 0 || r40 < 0) {
      showToast("Trucking rates must be valid non-negative numbers", "err"); return;
    }
    setSavingTruck(true);
    try {
      const r = await api.put("/settings/trucking-rates", { rate_20ft_usd: r20, rate_40ft_usd: r40 });
      setTruck(r.data);
      showToast("Trucking rates updated");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Save failed", "err");
    } finally {
      setSavingTruck(false);
    }
  };

  const addLine = () => {
    const v = newLine.trim();
    if (!v) return;
    if (lines.find((l) => l.toLowerCase() === v.toLowerCase())) {
      showToast("Already in the list", "err"); return;
    }
    setLines([...lines, v]);
    setNewLine("");
  };
  const removeLine = (l) => setLines(lines.filter((x) => x !== l));

  const saveLines = async () => {
    if (lines.length === 0) { showToast("Need at least one shipping line", "err"); return; }
    setSavingLines(true);
    try {
      const r = await api.put("/settings/shipping-lines", { values: lines });
      setLines(r.data.values);
      showToast("Shipping lines saved");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Save failed", "err");
    } finally {
      setSavingLines(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="overline mb-2">Settings</div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight leading-tight">
          Rates &amp; lines.
        </h1>
        <p className="mt-2 text-slate-600">Set the per-container charges and the list of shipping lines you operate for.</p>
      </div>

      {/* Inspection rates */}
      <section data-testid="settings-inspection-section">
        <div className="overline mb-3 flex items-center gap-2">
          <DollarSign size={12} /> Inspection · Yearly Fixed Rates
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3 flex items-start gap-2 text-sm text-amber-900" data-testid="settings-info">
          <Info size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <span>Auto-applied to every inspection gate-in entry. Existing records aren't changed.</span>
        </div>
        <form onSubmit={submitInsp} className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="settings-inspection-form">
          <Field label="Inspection & Service" prefix="USD" suffix="/ container">
            <input type="number" step="0.01" min="0" value={inspForm.inspection_charge_usd}
              onChange={(e) => setInspForm({ ...inspForm, inspection_charge_usd: e.target.value })}
              data-testid="settings-usd" className="form-input flex-1 font-mono-tabular text-base" />
          </Field>
          <Field label="Toll & Vann" prefix="GHC" suffix="/ container">
            <input type="number" step="0.01" min="0" value={inspForm.toll_vann_ghc}
              onChange={(e) => setInspForm({ ...inspForm, toll_vann_ghc: e.target.value })}
              data-testid="settings-ghc" className="form-input flex-1 font-mono-tabular text-base" />
          </Field>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-500">{insp?.updated_at ? `Updated ${new Date(insp.updated_at).toLocaleString()}` : "Defaults"}</div>
            <button type="submit" disabled={savingInsp} data-testid="settings-save-btn"
              className="inline-flex items-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-50 text-white px-5 py-2.5 rounded-md font-semibold transition-all hover:-translate-y-[1px]">
              <Save size={16} /> {savingInsp ? "Saving..." : "Save inspection rates"}
            </button>
          </div>
        </form>
      </section>

      {/* Trucking rates */}
      <section data-testid="settings-trucking-section">
        <div className="overline mb-3 flex items-center gap-2">
          <Truck size={12} /> Trucking · Per-Trip Rates
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3 flex items-start gap-2 text-sm text-amber-900">
          <Info size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <span>Rate is auto-picked based on container size: <strong>20ft</strong> for 20STD/22GP/etc, <strong>40ft</strong> for 40HC/45GP/etc.</span>
        </div>
        <form onSubmit={submitTruck} className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="settings-trucking-form">
          <Field label="20ft Container" prefix="USD" suffix="/ trip">
            <input type="number" step="0.01" min="0" value={truckForm.rate_20ft_usd}
              onChange={(e) => setTruckForm({ ...truckForm, rate_20ft_usd: e.target.value })}
              data-testid="settings-trucking-20" className="form-input flex-1 font-mono-tabular text-base" />
          </Field>
          <Field label="40 / 45 ft Container" prefix="USD" suffix="/ trip">
            <input type="number" step="0.01" min="0" value={truckForm.rate_40ft_usd}
              onChange={(e) => setTruckForm({ ...truckForm, rate_40ft_usd: e.target.value })}
              data-testid="settings-trucking-40" className="form-input flex-1 font-mono-tabular text-base" />
          </Field>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-500">{truck?.updated_at ? `Updated ${new Date(truck.updated_at).toLocaleString()}` : "Defaults"}</div>
            <button type="submit" disabled={savingTruck} data-testid="settings-trucking-save-btn"
              className="inline-flex items-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-50 text-white px-5 py-2.5 rounded-md font-semibold transition-all hover:-translate-y-[1px]">
              <Save size={16} /> {savingTruck ? "Saving..." : "Save trucking rates"}
            </button>
          </div>
        </form>
      </section>

      {/* Shipping lines */}
      <section data-testid="settings-lines-section">
        <div className="overline mb-3 flex items-center gap-2">
          <Building2 size={12} /> Shipping Lines
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="settings-lines-form">
          <p className="text-sm text-slate-600">The shipping lines that appear as button options on the Trucking gate-in page.</p>
          <div className="flex flex-wrap gap-2" data-testid="settings-lines-list">
            {lines.map((l) => (
              <span key={l} data-testid={`settings-line-${l}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-[#0a2952] text-sm font-semibold rounded-md">
                {l}
                <button type="button" onClick={() => removeLine(l)} data-testid={`settings-line-remove-${l}`}
                  className="text-slate-400 hover:text-red-600">
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="text" value={newLine} onChange={(e) => setNewLine(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLine(); } }}
              placeholder="Add a shipping line (e.g. Maersk, MSC)" data-testid="settings-line-input"
              className="form-input flex-1" />
            <button type="button" onClick={addLine} data-testid="settings-line-add-btn"
              className="inline-flex items-center gap-1 px-4 py-2.5 rounded-md bg-[#0a2952] text-white text-sm font-semibold hover:bg-[#071f40]">
              <Plus size={14} /> Add
            </button>
          </div>
          <div className="flex items-center justify-end pt-2 border-t border-slate-100">
            <button type="button" onClick={saveLines} disabled={savingLines} data-testid="settings-lines-save-btn"
              className="inline-flex items-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-50 text-white px-5 py-2.5 rounded-md font-semibold">
              <Save size={16} /> {savingLines ? "Saving..." : "Save shipping lines"}
            </button>
          </div>
        </div>
      </section>

      {toast && (
        <div data-testid="toast" className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-md shadow-lg text-sm font-semibold ${
          toast.kind === "err" ? "bg-red-600 text-white" : "bg-[#0a2952] text-white"
        }`}>{toast.msg}</div>
      )}
    </div>
  );
}

function Field({ label, prefix, suffix, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 block">{label}</span>
      <div className="flex items-center gap-2">
        {prefix && <span className="px-3 py-2.5 bg-slate-100 border border-slate-300 rounded-md font-mono-tabular text-sm font-semibold text-slate-700">{prefix}</span>}
        {children}
        {suffix && <span className="text-xs text-slate-500 whitespace-nowrap">{suffix}</span>}
      </div>
    </label>
  );
}
